import {
  CheckIcon,
  DotsHorizontalIcon,
  EyeIcon,
  LinkIcon,
  PencilIcon,
  TrashIcon,
} from "icons/outline";
import Link from "next/link";
import { Tooltip, useCopyToClipboard } from "ui";
import EditPopOverForm from "../EditPopOverForm";
import { useState } from "react";
import { TableConfirmation } from "./ButtonWithConfirmModal";

type ButtonActionGroupProps = {
  formURL: string;
  tableName: string;
  editEnabled: boolean;
  deleteEnabled: boolean;
  row: { id: string; original: any };
  previews: any[];
  refreshTable: () => void;
  getPreviewPath?: (row: { id: string }) => string | undefined;
  onEditItemClick: (id: string, row?: any, preview?: any) => void;
  onDeleteItemClick: (id: string) => void;
  showSuccessMessage: ({
    name,
    action,
  }: {
    name: string;
    action: string;
  }) => void;
};

const ButtonActionGroup: React.FC<ButtonActionGroupProps> = ({
  row,
  previews,
  getPreviewPath,
  formURL,
  tableName,
  refreshTable,
  editEnabled,
  deleteEnabled,
  onEditItemClick,
  onDeleteItemClick,
  showSuccessMessage,
}) => {
  // Define state not used outside (as React Table runs the plugins at first time only and state value become stale)
  const [editPopOverFormOpen, setEditPopOverFormOpen] = useState(false);
  const { copy, isCopied } = useCopyToClipboard();
  const [confirmModalOpened, setConfirmModalOpened] = useState(false);

  const href = getPreviewPath?.(row);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <div className="relative p-1.5">
        <div className="group/actions absolute right-0 top-0 flex flex-row-reverse divide-x divide-x-reverse divide-foundations-subtle overflow-hidden rounded-md border border-transparent bg-foundations-default text-foundations-subtler hover:border-foundations-subtle hover:shadow-3">
          <button className="p-1.5 focus:outline-none">
            <DotsHorizontalIcon className="h-5 w-5" />
          </button>

          {deleteEnabled && (
            <Tooltip disableHoverablePopup={true}>
              <Tooltip.Trigger
                delay={0}
                className="hidden p-1.5 hover:bg-foundations-subtlest hover:text-info  focus:outline-none group-hover/actions:block"
                onClick={() => setConfirmModalOpened(true)}
              >
                <TrashIcon className="h-5 w-5" />
              </Tooltip.Trigger>
              <Tooltip.Popup size="sm">Delete</Tooltip.Popup>
            </Tooltip>
          )}

          {editEnabled && (
            <Tooltip disableHoverablePopup={true}>
              <Tooltip.Trigger
                delay={0}
                className="hidden p-1.5 hover:bg-foundations-subtlest hover:text-info  focus:outline-none group-hover/actions:block"
                onClick={() => {
                  if (onEditItemClick) {
                    const preview = previews?.find((p) => p.id === row?.id);

                    onEditItemClick(row.id, row.original, preview);
                  } else {
                    setEditPopOverFormOpen(true);
                  }
                }}
              >
                <PencilIcon className="h-5 w-5" />
              </Tooltip.Trigger>
              <Tooltip.Popup size="sm">Edit</Tooltip.Popup>
            </Tooltip>
          )}

          {href && (
            <>
              <Tooltip disableHoverablePopup={true}>
                <Tooltip.Trigger
                  delay={0}
                  className={`hidden p-1.5 hover:bg-foundations-subtlest focus:outline-none  group-hover/actions:block ${
                    isCopied ? "hover:text-success" : "hover:text-info"
                  }`}
                  onClick={() => copy(`${window.location.origin}${href}`)}
                >
                  {isCopied ? (
                    <CheckIcon className="h-5 w-5" />
                  ) : (
                    <LinkIcon className="h-5 w-5" />
                  )}
                </Tooltip.Trigger>
                <Tooltip.Popup size="sm">Copy Link</Tooltip.Popup>
              </Tooltip>

              <Tooltip disableHoverablePopup={true}>
                <Tooltip.Trigger
                  delay={0}
                  render={<span className="hidden group-hover/actions:block" />}
                >
                  <Link
                    href={href}
                    className="hidden p-1.5 hover:bg-foundations-subtlest hover:text-info focus:outline-none group-hover/actions:block"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </Link>
                </Tooltip.Trigger>
                <Tooltip.Popup size="sm">View</Tooltip.Popup>
              </Tooltip>
            </>
          )}
        </div>

        {/* Placeholder */}
        <div aria-hidden className="h-5 w-5" />
      </div>
      <EditPopOverForm
        open={editPopOverFormOpen}
        setOpen={setEditPopOverFormOpen}
        url={`${formURL}${row.id}/`}
        title={`Edit ${tableName}`}
        onSuccess={(name: string) => {
          refreshTable();
          showSuccessMessage({ name, action: "edited" });
        }}
      />
      <TableConfirmation
        open={confirmModalOpened}
        setOpen={setConfirmModalOpened}
        tableName={tableName}
        rowsCount={1}
        dataCount={1}
        label={"Delete"}
        onConfirm={() => {
          onDeleteItemClick(row.id);
        }}
        actionForConfirmation={{ type: "delete_selected", method: "" }}
      />
    </div>
  );
};

export default ButtonActionGroup;
